import React, { useEffect, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import './Services.css';
import useReveal from '../hooks/useReveal';
import TokenRevealText from '../components/TokenRevealText';

const services = [
  {
    id: 'product-engineering',
    title: 'Product Engineering',
    description:
      'We transform product concepts into manufacturable designs using advanced engineering tools and industry expertise.',
    image: 'Engineer Working on CAD Design',
    capabilities: [
      'Concept Development',
      'Technical Specifications',
      'Part Design',
      'Prototype Development',
      'Assembly Simulation',
      'Product Testing & Validation',
    ],
  },
  {
    id: 'mechanical-design',
    title: 'Mechanical Design',
    description:
      'Precision mechanical design, CAD modeling, and DFM reviews that optimize performance and manufacturability.',
    image: 'Mechanical CAD Design',
    capabilities: [
      'CAD Modeling',
      'Design for Manufacturing (DFM)',
      'Tolerance Analysis',
      'Feasibility Study',
      'Process Analysis',
      'Design Validation',
    ],
  },
  {
    id: 'tooling-mold',
    title: 'Tooling & Mold',
    description:
      'High-precision tooling and mold development with advanced machining, finishing, and quality control.',
    image: 'Machining Process / Mold Block',
    capabilities: [
      'CNC & CAM Machining',
      'Fitting and Finishing',
      'Pilot Production',
      'Surface Texturing',
      'Sample Trials',
      'Quality Control',
    ],
  },
  {
    id: 'manufacturing',
    title: 'Manufacturing',
    description:
      'Our modern manufacturing facility enables high-precision production at scale with strict process control.',
    image: 'Production Facility',
    capabilities: [
      'Material Selection',
      'Injection Molding Production',
      'Progressive Cutting',
      'Manual Operations',
      'Inspection and Quality Assurance',
      'Packing and Dispatch',
    ],
  },
  {
    id: 'analogics-c2p',
    title: 'Analogics + C2P',
    description:
      'Electronics, firmware, and hardware development aligned with mechanical engineering and manufacturing to deliver complete products.',
    image: 'Analogics and C2P integration flow',
    capabilities: [
      'Electronics',
      'PCB / Firmware',
      'Mechanical',
      'Enclosures / Tooling',
      'System Integration',
      'Final Product',
    ],
  },
];

const Services = () => {
  const [active, setActive] = useState('product-engineering');
  const selected = services.find((s) => s.id === active);
  useReveal();
  const { hash } = useLocation();

  const tabsRef = useRef(null);

  useEffect(() => {
    if (!hash) return;
    const id = hash.replace('#', '');
    const match = services.find((s) => s.id === id);
    if (match) setActive(match.id);
  }, [hash]);

  const serviceImageSrc = {
    'product-engineering': '/images/AR7A4825.JPG',
    'mechanical-design': '/images/AR7A4716.jpg',
    'tooling-mold': '/images/AR7A4722.jpg',
    manufacturing: '/images/C2P-6.jpg',
    'analogics-c2p': '/images/production-unit-1.jpg',
  };

  const analogicsC2pImages = [
    '/images/production-unit-1.jpg',
    '/images/production-unit-2.jpg',
  ];

  return (
    <main className="services-page page-enter">
      {/* Page Hero */}
      <section className="page-hero">
        <div className="container reveal">
          <p className="section-eyebrow-light"><TokenRevealText text="What We Offer" speed="medium" /></p>
          <h1><TokenRevealText text="Our Services" speed="medium" /></h1>
          <p>
            <TokenRevealText
              text="End-to-end engineering and manufacturing services from concept to production."
              speed="medium"
            />
          </p>
        </div>
      </section>

      {/* ===== SERVICE TABS ===== */}
      <section className="section services-tabs-section">
        <div className="container" ref={tabsRef}>
          <div className="service-anchors" aria-hidden="true">
            {services.map((s) => (
              <span key={s.id} id={s.id} className="service-anchor" />
            ))}
          </div>
          {/* Tab Buttons */}
          <div className="service-tabs reveal">
            {services.map((s) => (
              <button
                key={s.id}
                className={`service-tab ${active === s.id ? 'active' : ''}`}
                onClick={() => setActive(s.id)}
              >
                <span><TokenRevealText text={s.title} speed="medium" /></span>
              </button>
            ))}
          </div>

          {/* Tab Content */}
          {selected && (
            <div className="service-content grid-2 anim-fade-up reveal" key={selected.id}>
              <div>
                <h2 className="section-title"><TokenRevealText text={selected.title} speed="medium" /></h2>
                <div className="divider" />
                <p className="body-text"><TokenRevealText text={selected.description} speed="medium" /></p>
                <h4 className="cap-heading"><TokenRevealText text="Our Capabilities" speed="medium" /></h4>
                <ul className="cap-list">
                  {selected.capabilities.map((cap, i) => (
                    <li key={cap} style={{ animationDelay: `${0.05 * i}s` }}>
                      <span className="cap-check">&#10003;</span>
                      <TokenRevealText text={cap} speed="medium" />
                    </li>
                  ))}
                </ul>
              </div>
              <div className="media-card anim-fade-right" style={{ height: '380px' }}>
                {selected.id === 'analogics-c2p' ? (
                  <div className="service-media-split">
                    {analogicsC2pImages.map((src) => (
                      <img
                        key={src}
                        src={src}
                        alt="Analogics and C2P facility"
                        onError={(e) => {
                          e.currentTarget.onerror = null;
                          e.currentTarget.src = '/images/C2P3.jpg';
                        }}
                      />
                    ))}
                  </div>
                ) : (
                  <img
                    src={serviceImageSrc[selected.id] || '/images/C2P3.jpg'}
                    alt={selected.image}
                    onError={(e) => {
                      e.currentTarget.onerror = null;
                      e.currentTarget.src = '/images/C2P3.jpg';
                    }}
                  />
                )}
              </div>
            </div>
          )}
        </div>
      </section>

      {/* ===== PROCESS ===== */}
      <section className="section process-section">
        <div className="container">
          <div className="section-head-center reveal">
            <p className="section-eyebrow"><TokenRevealText text="Process Flow" speed="medium" /></p>
            <div className="rule" />
          </div>
          <div className="process-visual reveal">
            <img
              src={`${process.env.PUBLIC_URL}/images/process-flow.png`}
              alt="Process flow"
            />
          </div>
        </div>
      </section>

      <section className="services-cta">
        <div className="container services-cta-inner">
          <h2><TokenRevealText text="Ready to discuss your next project?" speed="medium" /></h2>
          <p><TokenRevealText text="Let’s align on scope, timelines, and the right engineering approach." speed="medium" /></p>
          <Link to="/contact" className="btn-primary">Let's Connect</Link>
        </div>
      </section>
    </main>
  );
};

export default Services;